import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import ObjectiveCard from "../components/EditBoard/ObjectiveCard";
import AddButton from "../components/AddButton/AddButton";
import TotalSVG from "../components/EditBoard/TotalSVG";
import "../styles/board.css"
import "../styles/edit-board.css"
import Alert from "../components/Alert/Alert";
import { usePromptOnLeave } from './../hooks/usePromptOnLeave';
import { getBoard, checkBoardAccess } from "../api/boards";
import { saveObjectives as apiSaveObjectives } from "../api/okr";
import Masonry from '@mui/lab/Masonry';
import {
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';

const EditBoard = () => {
  const { t } = useTranslation();
  const { company_slug, board_slug } = useParams();
  const [boardName, setBoardName] = useState("");
  const [boardSlug, setBoardSlug] = useState("");
  const [initialBoardName, setInitialBoardName] = useState("");
  const [initialBoardSlug, setInitialBoardSlug] = useState("");
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [editingBoardName, setEditingBoardName] = useState(false);
  const [editingBoardSlug, setEditingBoardSlug] = useState(false);
  const [objectives, setObjectives] = useState([]);
  const [initialObjectives, setInitialObjectives] = useState([]);
  const [resetSignal, setResetSignal] = useState(0);
  const [alert, setAlert] = useState({ show: false, text: "", type: "" });
  const [boardSlugError, setBoardSlugError] = useState("");

  const [isSticky, setIsSticky] = useState(false);
  const [height, setHeight] = useState(0);
  const distanceRef = useRef(0);

  const headerRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchBoardData = async () => {
      try {
        const access = await checkBoardAccess(company_slug, board_slug);
        if (!access.is_admin) {
          navigate("/dashboard");
          return;
        }

        const data = await getBoard(company_slug, board_slug);
        if (data.board_status === "archived") {
          navigate("/dashboard");
          return;
        }

        setBoardName(data.board_name || t('edit_board.title_placeholder'));
        setBoardSlug(data.board_slug);
        setInitialBoardName(data.board_name);
        setInitialBoardSlug(data.board_slug);

        // Цілі з БД вважаємо кастомними — їх ваги не перераховуємо
        const objectivesFromDB = (data.objectives || []).map((obj) => ({
          ...obj,
          isWeightCustomized: true,
        }));

        setObjectives(objectivesFromDB);
        setInitialObjectives(objectivesFromDB);
      } catch (err) {
        console.error("Помилка завантаження борда:", err);
      }
    };
    fetchBoardData();
  }, [company_slug, board_slug, navigate, t]);

  useEffect(() => {
    if (headerRef.current) {
      distanceRef.current = headerRef.current.getBoundingClientRect().top + window.scrollY;
      setHeight(headerRef.current.offsetHeight);
    }

    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition >= distanceRef.current) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // 🔹 Перераховує ваги лише для карток без кастомної ваги.
  // Картки з isWeightCustomized: true залишаються незмінними.
  // Решта рівномірно ділять те, що залишилось від 100%.
  const recalculateWeights = (list) => {
    if (list.length === 0) return list;

    const customized = list.filter((obj) => obj.isWeightCustomized);
    const auto = list.filter((obj) => !obj.isWeightCustomized);

    if (auto.length === 0) return list;

    const customizedTotal = customized.reduce((sum, obj) => sum + parseFloat(obj.weight || 0), 0);
    const remaining = Math.max(0, 100 - customizedTotal);
    const autoWeight = parseFloat((remaining / auto.length).toFixed(1));

    return list.map((obj) =>
      obj.isWeightCustomized ? obj : { ...obj, weight: autoWeight }
    );
  };

  const addObjective = () => {
    setObjectives((prevObjectives) => {
      const newObjective = {
        id: Date.now(),
        title: "",
        weight: 0,
        type: "",
        key_results: [],
        isWeightCustomized: false, // нова картка — автоматична вага
      };
      return recalculateWeights([...prevObjectives, newObjective]);
    });
    setIsSaveEnabled(true);
  };

  const deleteObjective = (id) => {
    setObjectives((prevObjectives) => {
      const filtered = prevObjectives.filter((obj) => obj.id !== id);
      return recalculateWeights(filtered);
    });
    setIsSaveEnabled(true);
  };

  const updateObjective = (id, updatedObjective) => {
    setObjectives((prevObjectives) =>
      prevObjectives.map((obj) => {
        if (obj.id !== id) return obj;

        // Якщо явно змінюється вага — позначаємо як кастомну
        const isWeightCustomized =
          "weight" in updatedObjective
            ? true
            : obj.isWeightCustomized;

        return { ...obj, ...updatedObjective, isWeightCustomized };
      })
    );
    setIsSaveEnabled(true);
  };

  const handleSaveChanges = async () => {
    const payload = {
      board_name: boardName,
      current_slug: initialBoardSlug,
      board_slug: boardSlug,
      company_slug,
      objectives: objectives.map((obj) => ({
        id: obj.id,
        title: obj.title,
        weight: obj.weight,
        type: obj.type,
        // isWeightCustomized — суто фронтендове поле, не відправляємо
        key_results: obj.key_results.map((kr) => ({
          id: kr.id,
          title: kr.title,
          deadline: kr.deadline,
          champion_name: kr.champion_name,
          champion_user_id: kr.champion_user_id || null,
          weight: kr.weight,
          type: kr.type,
          initial_value: kr.initial_value,
          target_value: kr.target_value,
          initiatives: kr.initiatives.map((init) => ({
            title: init.title,
            is_completed: init.is_completed,
            completed_at: init.completed_at,
            weight: init.weight,
          })),
        })),
      })),
    };

    try {
      await apiSaveObjectives(payload);

      if (initialBoardSlug !== boardSlug) {
        setAlert({ show: true, text: t('edit_board.success'), type: "success" });
        setIsSaveEnabled(false);
        setTimeout(() => {
          navigate(`/board/${company_slug}/${boardSlug}/edit`);
        }, 500);
      } else {
        setAlert({ show: true, text: t('edit_board.success'), type: "success" });
        setInitialBoardSlug(boardSlug);
        setIsSaveEnabled(false);
      }
    } catch (err) {
      setAlert({ show: true, text: err.message || t('edit_board.error'), type: "error" });
      console.error("Помилка збереження цілей:", err);
    }
  };

  const handleResetChanges = () => {
    const newObjectives = JSON.parse(JSON.stringify(initialObjectives));

    setBoardName(initialBoardName);
    setBoardSlug(initialBoardSlug);
    setObjectives(newObjectives);
    setResetSignal((prev) => prev + 1);
    setIsSaveEnabled(false);
  };

  const { showModal, onConfirm, onCancel } = usePromptOnLeave(
    t('common.unsaved_changes'),
    isSaveEnabled
  );

  const validateBoardSlug = (value) => {
    const slugPattern = /^[a-zA-Z0-9_-]+$/;
    if (!value.trim()) {
      setIsSaveEnabled(false);
      return t('common.required');
    } else if (!slugPattern.test(value)) {
      setIsSaveEnabled(false);
      return t('common.slug_hint');
    } else {
      setIsSaveEnabled(true);
      return;
    }
  };

  return (
    <>
    {isSticky && <div style={{ height: `${height}px` }}></div>}
    <div
      className={`edit-board-header ${isSticky ? "sticky" : ""}`}
      ref={headerRef}
    >
       <div className="container">
        <div className="header-btn-box">
          <Link className="thirdly-button" to="/dashboard">← <span>{t('edit_board.my_boards')}</span></Link>
          <div>
            <button
              onClick={handleResetChanges}
              disabled={!isSaveEnabled}
              className="button secondary-button cancelButton"
            >
              {t('edit_board.reset')}
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={!isSaveEnabled}
              className="button primary-button saveButton"
            >
              {t('edit_board.save')}
            </button>
          </div>
        </div>
      </div>
    </div>  
    <div className="edit-board-page okr">
      {alert.show && (
        <Alert
          text={alert.text}
          type={alert.type}
          onClose={() => setAlert({ show: false, text: "", type: "" })}
        />
        )}
      <div className="okr-top">
        <div className="okr-total">
          <div>
                <div className="ok-label-row">
                    <p className="ok-label">{t('edit_board.board_name')}</p>
                    {editingBoardName ? (
                    <input
                        className="ok-label-value"
                        type="text"
                        value={boardName}
                        onChange={(e) => { setBoardName(e.target.value); setIsSaveEnabled(true); }}
                        onBlur={() => {
                        setEditingBoardName(false);
                        if (!boardName.trim()) setBoardName(t('edit_board.title_placeholder'));
                        }}
                        autoFocus
                    />
                    ) : (
                    <p className="ok-label-value" onClick={() => setEditingBoardName(true)}>{boardName || t('edit_board.title_placeholder')}</p>
                    )}
                </div>
                <div className="ok-label-row">
                    <p className="ok-label">{t('edit_board.board_id')}</p>
                    {editingBoardSlug ? (
                      <input
                          className="ok-label-value"
                          type="text"
                          style={{ borderBottom: true ? '1px solid red !important' : '' }}
                          value={boardSlug}
                          onChange={(e) => { 
                            setBoardSlug(e.target.value); 
                            setIsSaveEnabled(true); 
                            const error = validateBoardSlug(e.target.value);
                            setBoardSlugError(error);
                          }}
                          onBlur={() => {
                          setEditingBoardSlug(false);
                          }}
                          autoFocus
                      />
                    ) : (
                        <p className="ok-label-value" onClick={() => setEditingBoardSlug(true)}>{boardSlug || initialBoardSlug}</p>
                    )}
                    {boardSlugError && <span style={{fontSize: "12px"}}>{boardSlugError}</span>}
                </div>
              </div>
              <div className="okr-total-bottom">
                <svg xmlns="http://www.w3.org/2000/svg" width="61" height="56" viewBox="0 0 61 56" fill="none">
                  <path d="M42.6614 53.0202C22.786 36.2367 21.5375 12.7961 21.5375 12.7961" stroke="white" strokeOpacity="0.8" strokeWidth="1.4" strokeMiterlimit="10"/>
                  <path d="M15.7309 15.6545L19.7711 4.22948L31.1814 15.9235" stroke="white" strokeOpacity="0.8" strokeWidth="1.4" strokeMiterlimit="10"/>
                </svg>
                <p>{t('edit_board.slug_hint')} {window.location.hostname}/board/{company_slug}/<b>{boardSlug}</b></p>
              </div>
        </div>
        <TotalSVG totalPprogress={0} />
      </div>

      <h2>{t('edit_board.section_title')}</h2>
        <Masonry columns={{ lg: 3, md: 2, sm: 1, xs: 1 }} spacing={3} sequential sx={{ width: "auto", marginTop: "10px" }}>
          {objectives.map((objective, index) => (
            <ObjectiveCard 
                key={`objective-${objective.id}` || `objective-${index}`}
                objective={objective} 
                updateObjective={updateObjective} 
                deleteObjective={deleteObjective}
                resetSignal={resetSignal}
            />
          ))}
          <AddButton onClick={addObjective} title={t('edit_board.add_objective')} />
        </Masonry>
      </div>
      <Dialog
        open={showModal}
        onClose={onCancel}
        aria-labelledby="unsaved-changes-title"
        aria-describedby="unsaved-changes-description"
        PaperProps={{ className: "dialogPaper" }}
      >
        <DialogContent
        sx={{
          backgroundColor: "var(--base-color)",
          color: "var(--inverse-color)"
        }}
        >
          <p sx={{
            padding: "30px 25px",
            fontFamily: "Inerta",
          }}>
            {t('common.unsaved_changes')}
          </p>
        </DialogContent>
        <DialogActions sx={{backgroundColor: "var(--base-color)", padding: "20px"}}>
          <button onClick={onCancel} className="button secondary-button cancelButton">
            {t('common.cancel_short')}
          </button>
          <button onClick={onConfirm} className="button primary-button saveButton">
            {t('common.leave')}
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditBoard;